/**
 * Single source of truth for OrderStatus wire-form parsing.
 *
 * Wave 1 of the SoT plan. Replaces the six parallel mapping tables that
 * grew organically across packages (MappingService.FE_TO_BE_STATUS_MAP,
 * floristy STATUS_API_TO_DOMAIN / STATUS_DOMAIN_TO_API, storefront
 * STATUS_API_TO_ENUM, storefront types.ts OrderStatus enum,
 * mappingService.mapStatusToFrontendSimple inline statusMap).
 *
 * Contract:
 *   - Canonical wire form is UPPER_CASE (BackendOrderStatus / Prisma enum).
 *   - Legacy kebab-case is accepted on input (deprecation window) but never
 *     emitted from any new code path.
 *   - parseOrderStatus(unknown) -> BackendOrderStatus | null. Never throws
 *     on user input; caller decides whether to log/drop.
 *   - assertOrderStatus(s) -> asserts canonical form. Throws on dirty value
 *     (programmer error / invariant breach).
 *
 * Usage rule (see docs/architecture/STATUS_CONTRACT.md):
 *   - Mapping lives at the bounded-context boundary only:
 *     * backend route HTTP edge (req.query.status / body.status)
 *     * backend serializer on outgoing response
 *     * frontend repository edge (DTO -> domain)
 *   - Hooks, components, services, utils MUST NOT contain a status map.
 */
import { ORDER_STATUS } from '../constants/orderStatuses.js';
/** Set of all canonical wire values for O(1) membership check. */
const CANONICAL = new Set(Object.values(ORDER_STATUS));
/**
 * Legacy kebab-case form. Generated mechanically from ORDER_STATUS keys
 * so the table cannot drift out of sync with the canonical enum: any
 * future ORDER_STATUS addition is automatically supported here.
 */
const KEBAB_LEGACY = Object.freeze(Object.fromEntries(Object.values(ORDER_STATUS).map((v) => [
    v.toLowerCase().replace(/_/g, '-'),
    v,
])));
/**
 * Parse user input into a canonical BackendOrderStatus.
 *
 * Accepts:
 *   - canonical UPPER_CASE ('PENDING_PAYMENT')
 *   - legacy kebab-case ('pending-payment')
 *
 * Returns null for everything else (typos, garbage, wrong types). Caller
 * decides whether to log a WARN, return 400, or fall back to a default —
 * never silently drop into `.filter(Boolean)` (Hard Rule 4).
 */
export function parseOrderStatus(raw) {
    if (typeof raw !== 'string')
        return null;
    if (CANONICAL.has(raw))
        return raw;
    return KEBAB_LEGACY[raw] ?? null;
}
/**
 * Invariant: must already be canonical UPPER_CASE.
 * Throws on dirty value — use this when the value comes from internal
 * code (typed already) and a non-canonical form is a programming error.
 */
export function assertOrderStatus(s) {
    if (!CANONICAL.has(s)) {
        throw new Error(`Not a canonical OrderStatus: "${s}". ` +
            `Expected one of: ${Object.values(ORDER_STATUS).join(', ')}.`);
    }
}
/**
 * Convenience: type guard for narrowing without throw.
 * Equivalent to `parseOrderStatus(s) === s` but reads as a guard.
 */
export function isOrderStatus(s) {
    return typeof s === 'string' && CANONICAL.has(s);
}
